import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Save, Pencil, X, Plus, Ban, RotateCcw } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

const LOCATION_TYPES = ['yard', 'mrf', 'landfill', 'transfer_station', 'maintenance', 'office', 'department', 'overhead'];

const emptyForm = { code: '', name: '', division: '', location_type: '', sort_order: '' };

function toEditForm(cc) {
  return {
    code: cc.code, name: cc.name, division: cc.division || '',
    location_type: cc.location_type || '', sort_order: String(cc.sort_order ?? ''),
  };
}

// overtime_cost_centers (migration 008) confirmed with Robert as the one
// source of truth: every extract-resolution parser in the portal (Overtime,
// HR Headcount, Staffing, Inventory, Fuel, Expenses, Fleet Dashboard, Sales)
// resolves a raw "123 Some Name" string against this table by leading code.
// Before this page, a new row could only ever arrive through a migration
// (008, 039, 114) or a direct database edit -- which is exactly how 121
// Payroll ended up in prod with real heads reporting against it but absent
// from every migration, so dev never had it.
export default function CostCenterMapping() {
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const { data: costCenters = [], isLoading } = useQuery({
    queryKey: ['admin-overtime-cost-centers', showInactive],
    queryFn: () => api.get('/admin/overtime-cost-centers', { params: showInactive ? { include_inactive: 'true' } : {} }),
  });

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/admin/overtime-cost-centers', payload),
    onSuccess: () => {
      toast.success('Cost center added — every upload in the portal can resolve it now.');
      setAdding(false); setAddForm(emptyForm);
      qc.invalidateQueries({ queryKey: ['admin-overtime-cost-centers'] });
    },
    onError: (err) => toast.error(err.message || 'Could not add that cost center.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/admin/overtime-cost-centers/${id}`, payload),
    onSuccess: () => {
      toast.success('Saved.');
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['admin-overtime-cost-centers'] });
    },
    onError: (err) => toast.error(err.message || 'Could not save.'),
  });

  function startEdit(cc) {
    setAdding(false);
    setEditingId(cc.id);
    setEditForm(toEditForm(cc));
  }

  function submitAdd() {
    createMutation.mutate({
      code: addForm.code.trim(), name: addForm.name.trim(),
      division: addForm.division.trim() || null, location_type: addForm.location_type || null,
      sort_order: addForm.sort_order === '' ? undefined : parseInt(addForm.sort_order, 10),
    });
  }

  function submitEdit(id) {
    updateMutation.mutate({
      id,
      payload: {
        name: editForm.name.trim(), division: editForm.division.trim() || null,
        location_type: editForm.location_type || null,
        sort_order: editForm.sort_order === '' ? undefined : parseInt(editForm.sort_order, 10),
      },
    });
  }

  function toggleActive(cc) {
    const verb = cc.is_active ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${verb} ${cc.code} ${cc.name}? Nothing is deleted — this only changes whether uploads and dropdowns can resolve to it.`)) return;
    updateMutation.mutate({ id: cc.id, payload: { is_active: !cc.is_active } });
  }

  const divisions = [...new Set(costCenters.map((cc) => cc.division).filter(Boolean))].sort();

  if (isLoading) return <LoadingSpinner />;

  return (
    <div style={s.page}>
      <datalist id="cc-divisions">
        {divisions.map((d) => <option key={d} value={d} />)}
      </datalist>
      <PageHeader
        eyebrow="Administration"
        title="Cost Centers"
        subtitle="The one list every upload in the portal resolves a raw cost center string against — Overtime, HR Headcount, Staffing, Inventory, Fuel, Expenses, Fleet Dashboard, and Sales all share this table. Deactivating one keeps its history but stops it appearing as a valid destination going forward."
        actions={
          !adding && (
            <button style={btn.primary} onClick={() => { setEditingId(null); setAdding(true); setAddForm(emptyForm); }}>
              <Plus size={14} /> Add Cost Center
            </button>
          )
        }
      />

      <div style={s.actionsRow}>
        <label style={s.checkLabel}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <span style={s.filterCount}>{costCenters.length} cost center{costCenters.length === 1 ? '' : 's'}</span>
      </div>

      <div style={s.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Code', 'Name', 'Division', 'Location Type', 'Status', ''].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adding && (
                <tr style={s.editRow}>
                  <td colSpan={6} style={s.editTd}>
                    <div style={s.editGrid}>
                      <Field label="Code"><input style={s.input} value={addForm.code} onChange={(e) => setAddForm({ ...addForm, code: e.target.value })} placeholder="e.g. 126" /></Field>
                      <Field label="Name"><input style={s.input} value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="e.g. New Facility" /></Field>
                      <Field label="Division"><input style={s.input} list="cc-divisions" value={addForm.division} onChange={(e) => setAddForm({ ...addForm, division: e.target.value })} placeholder="Optional" /></Field>
                      <Field label="Location Type">
                        <select style={s.input} value={addForm.location_type} onChange={(e) => setAddForm({ ...addForm, location_type: e.target.value })}>
                          <option value="">Optional</option>
                          {LOCATION_TYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
                        </select>
                      </Field>
                      <Field label="Sort Order"><input style={s.input} value={addForm.sort_order} onChange={(e) => setAddForm({ ...addForm, sort_order: e.target.value })} placeholder="Defaults to the code" /></Field>
                    </div>
                    <div style={s.editActions}>
                      <button style={s.cancelBtn} onClick={() => setAdding(false)}>Cancel</button>
                      <button style={s.saveBtn} onClick={submitAdd} disabled={!addForm.code || !addForm.name || createMutation.isPending}>
                        <Save size={13} /> {createMutation.isPending ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {costCenters.map((cc) => {
                const isEditing = editingId === cc.id;
                return (
                  <Fragment key={cc.id}>
                    <tr style={{ ...s.tr, opacity: cc.is_active ? 1 : 0.55 }}>
                      <td style={s.td}><strong>{cc.code}</strong></td>
                      <td style={s.td}>{cc.name}</td>
                      <td style={{ ...s.td, color: '#9CA3AF' }}>{cc.division || '—'}</td>
                      <td style={{ ...s.td, color: '#9CA3AF' }}>{cc.location_type || '—'}</td>
                      <td style={s.td}>
                        {cc.is_active
                          ? <span style={s.active}>Active</span>
                          : <span style={s.inactive}>Inactive</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button style={s.iconBtn} title="Edit" onClick={() => (isEditing ? setEditingId(null) : startEdit(cc))}>
                          {isEditing ? <X size={13} /> : <Pencil size={13} />}
                        </button>
                        <button
                          style={s.iconBtn} title={cc.is_active ? 'Deactivate' : 'Reactivate'}
                          onClick={() => toggleActive(cc)}
                        >
                          {cc.is_active ? <Ban size={13} /> : <RotateCcw size={13} />}
                        </button>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr style={s.editRow}>
                        <td colSpan={6} style={s.editTd}>
                          <div style={s.editGrid}>
                            <Field label="Code"><input style={{ ...s.input, background: '#F3F4F6' }} value={editForm.code} disabled title="Code cannot be changed — every parser resolves history by it." /></Field>
                            <Field label="Name"><input style={s.input} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></Field>
                            <Field label="Division"><input style={s.input} list="cc-divisions" value={editForm.division} onChange={(e) => setEditForm({ ...editForm, division: e.target.value })} /></Field>
                            <Field label="Location Type">
                              <select style={s.input} value={editForm.location_type} onChange={(e) => setEditForm({ ...editForm, location_type: e.target.value })}>
                                <option value="">—</option>
                                {LOCATION_TYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
                              </select>
                            </Field>
                            <Field label="Sort Order"><input style={s.input} value={editForm.sort_order} onChange={(e) => setEditForm({ ...editForm, sort_order: e.target.value })} /></Field>
                          </div>
                          <div style={s.editActions}>
                            <button style={s.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
                            <button style={s.saveBtn} onClick={() => submitEdit(cc.id)} disabled={!editForm.name || updateMutation.isPending}>
                              <Save size={13} /> {updateMutation.isPending ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {!costCenters.length && !adding && (
                <tr><td colSpan={6} style={s.empty}>No cost centers{showInactive ? '' : ' are active'}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={s.label}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  page:        { maxWidth: '1100px', margin: '0 auto' },
  actionsRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' },
  checkLabel:  { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' },
  filterCount: { fontSize: '11px', color: '#9CA3AF' },
  card:        { ...card.base, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.th },
  tr:          { ...tbl.tr },
  td:          { ...tbl.td },
  active:      { fontSize: '11px', fontWeight: 600, color: '#15803D', background: '#F0FDF4', padding: '2px 8px', borderRadius: '10px' },
  inactive:    { fontSize: '11px', fontWeight: 600, color: '#6B7280', background: '#F3F4F6', padding: '2px 8px', borderRadius: '10px' },
  iconBtn:     { ...btn.icon },
  editRow:     { borderBottom: '1px solid #F3F4F6', background: '#F9FAFB' },
  editTd:      { padding: '16px' },
  editGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '12px' },
  label:       { display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:       { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', background: '#fff' },
  editActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', fontSize: '12px', fontWeight: 600,
    border: 'none', borderRadius: '6px', background: '#1B2A4A',
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  cancelBtn: {
    padding: '8px 16px', fontSize: '12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', background: '#fff', color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
  },
  empty:       { textAlign: 'center', padding: '32px', color: '#9CA3AF' },
};
